import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImageUrl } from '../../utils/api';
import TemplateEditorWorkspace, { TEMPLATE_EDITOR_EXTENSIONS } from './TemplateEditorWorkspace';
import PlaceholdersSidebar from './PlaceholdersSidebar';
import EditableModalTitle from './EditableModalTitle';
import ErrorModal from './ErrorModal';
import {
  DEFAULT_TEMPLATE_FONT_SIZE,
  wrapTemplateContent,
  parseTemplateContent
} from '../../utils/templateEditorFontSize';
import './Salon.css';

const CreatePtoDecisionTemplateModal = ({ isOpen, onClose, salonId, onTemplateCreated, editingTemplate }) => {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({ title: '', is_active: true });
  const [loading, setLoading] = useState(false);

  useScrollLock(isOpen);
  const [error, setError] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [originalFontSize, setOriginalFontSize] = useState(DEFAULT_TEMPLATE_FONT_SIZE);
  const [fontSize, setFontSize] = useState(DEFAULT_TEMPLATE_FONT_SIZE);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const originalContentRef = useRef('');
  const originalFontSizeRef = useRef(DEFAULT_TEMPLATE_FONT_SIZE);
  const fontSizeRef = useRef(DEFAULT_TEMPLATE_FONT_SIZE);

  useEffect(() => {
    originalContentRef.current = originalContent;
  }, [originalContent]);

  useEffect(() => {
    originalFontSizeRef.current = originalFontSize;
  }, [originalFontSize]);

  useEffect(() => {
    fontSizeRef.current = fontSize;
  }, [fontSize]);

  const editor = useEditor({
    // Avoid accessing the view before EditorContent mounts (Firefox throws on editor.view)
    immediatelyRender: false,
    // Since TipTap 2.7/@tiptap/react v3, useEditor() no longer re-renders the component on every
    // transaction by default (a perf change) - without this, every isActive()/getAttributes()
    // check in the toolbar (bold, headings, alignment, image overlay, all of it) reads stale
    // state and never visibly updates as the cursor/selection moves.
    shouldRerenderOnTransaction: true,
    extensions: TEMPLATE_EDITOR_EXTENSIONS,
    content: '',
    // Without this, ProseMirror's HTML parser collapses runs of spaces down to one
    // (both on initial load and while typing) - this preserves them as typed.
    parseOptions: { preserveWhitespace: 'full' },
    editorProps: {
      attributes: { class: 'contract-editor-content' },
    },
    onUpdate: ({ editor }) => {
      const currentContent = editor.getHTML();
      setHasUnsavedChanges(
        currentContent !== originalContentRef.current
        || fontSizeRef.current !== originalFontSizeRef.current
      );
      setIsEditorEmpty(editor.getText().trim() === '');
    },
  });

  const fetchPlaceholders = useCallback(async () => {
    try {
      setLoadingPlaceholders(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/pto/decision-templates/placeholders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaceholders(response.data.placeholders || []);
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    } finally {
      setLoadingPlaceholders(false);
    }
  }, [salonId]);

  useEffect(() => {
    if (isOpen) {
      fetchPlaceholders();
    }
  }, [isOpen, fetchPlaceholders]);

  useEffect(() => {
    if (!isOpen || !editor || editor.isDestroyed) return;

    if (editingTemplate) {
      setFormData({
        title: editingTemplate.title || '',
        is_active: editingTemplate.is_active !== undefined ? editingTemplate.is_active : true
      });
      const { fontSize: parsedFontSize, html } = parseTemplateContent(editingTemplate.template_content || '');
      editor.commands.setContent(html, { parseOptions: { preserveWhitespace: 'full' } });
      setFontSize(parsedFontSize);
      setOriginalFontSize(parsedFontSize);
      setOriginalContent(html);
      setHasUnsavedChanges(false);
      setIsEditorEmpty(editor.getText().trim() === '');
    } else {
      setFormData({ title: '', is_active: true });
      editor.commands.setContent('');
      setOriginalContent('');
      setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
      setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
      setHasUnsavedChanges(false);
      setIsEditorEmpty(true);
    }
  }, [isOpen, editingTemplate, editor]);

  const handleFontSizeChange = (next) => {
    setFontSize(next);
    setHasUnsavedChanges(
      (editor?.getHTML() || '') !== originalContent || next !== originalFontSize
    );
  };

  const handleActiveChange = (e) => {
    setFormData(prev => ({ ...prev, is_active: e.target.checked }));
    setHasUnsavedChanges(true);
  };

  const handleTitleChange = (title) => {
    setFormData(prev => ({ ...prev, title }));
    setHasUnsavedChanges(true);
  };

  const insertPlaceholder = (placeholder) => {
    if (editor) {
      editor.commands.insertContent(`{{${placeholder}}}`);
      editor.commands.focus();
    }
  };

  const buildPayload = () => ({
    title: formData.title,
    template_content: wrapTemplateContent(editor.getHTML(), fontSize),
    is_active: formData.is_active
  });

  const validate = () => {
    if (!formData.title.trim()) {
      setError(t('settings.titleRequired'));
      return false;
    }
    if (!editor.getHTML() || editor.getHTML() === '<p></p>') {
      setError(t('settings.contentRequired'));
      return false;
    }
    return true;
  };

  const saveTemplate = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const payload = buildPayload();
      if (editingTemplate) {
        await axios.put(`/api/salons/${salonId}/pto/decision-templates/${editingTemplate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`/api/salons/${salonId}/pto/decision-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      return true;
    } catch (err) {
      console.error('Error saving PTO decision template:', err);
      setError(err.response?.data?.error || `Failed to ${editingTemplate ? 'update' : 'create'} template`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const ok = await saveTemplate();
    if (ok) {
      setOriginalContent(editor.getHTML());
      setOriginalFontSize(fontSize);
      setHasUnsavedChanges(false);
      onTemplateCreated();
      if (!editingTemplate || showUnsavedWarning) {
        handleClose();
      }
    }
  };

  const resetState = () => {
    setFormData({ title: '', is_active: true });
    setError('');
    setHasUnsavedChanges(false);
    setShowUnsavedWarning(false);
    setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    if (editor) {
      editor.commands.setContent('');
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      return;
    }
    resetState();
    onClose();
  };

  const handleDiscardChanges = () => {
    resetState();
    onClose();
  };

  const handleSaveAndClose = async () => {
    if (!validate()) return;
    const ok = await saveTemplate();
    if (ok) {
      onTemplateCreated();
      resetState();
      onClose();
    }
  };

  const handleCancelWarning = () => setShowUnsavedWarning(false);

  const uploadImage = async (file) => {
    const token = localStorage.getItem('token');
    const formDataPayload = new FormData();
    formDataPayload.append('image', file);
    const response = await axios.post(`/api/salons/${salonId}/pto/decision-templates/upload-image`, formDataPayload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    // Absolute URL (pointing at the actual API origin, not window.location.origin - those
    // differ in dev since CRA's dev server and Express run on different ports) so it's
    // unambiguous when the document is later rendered in the separately-opened print window.
    return getImageUrl(response.data.url);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal contract-template-modal">
        <div className="modal-header">
          <EditableModalTitle
            value={formData.title}
            onChange={handleTitleChange}
            placeholder={t('settings.enterTemplateTitle')}
            editLabel={t('settings.editTitle')}
          />
          <div className="modal-header-right">
            <label className="checkbox-label modal-header-active-toggle">
              <input type="checkbox" checked={formData.is_active} onChange={handleActiveChange} />
              <span className="checkmark"></span>
              {t('settings.active')}
            </label>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
        </div>

        <div className="contract-modal-content">
          <PlaceholdersSidebar description={t('settings.clickToInsert')}>
            {loadingPlaceholders ? (
              <div className="loading-spinner-small"></div>
            ) : placeholders.length === 0 ? (
              <div className="no-placeholders">
                <p>{t('settings.noPlaceholdersAvailable')}</p>
              </div>
            ) : (
              <div className="placeholders-list">
                {placeholders.map((placeholder, index) => (
                  <button
                    key={index}
                    type="button"
                    className="placeholder-button"
                    onClick={() => insertPlaceholder(placeholder.key)}
                    title={placeholder.description}
                  >
                    <span className="placeholder-key">{`{{${placeholder.key}}}`}</span>
                    <span className="placeholder-description">{placeholder.description}</span>
                  </button>
                ))}
              </div>
            )}
          </PlaceholdersSidebar>

          <div className="contract-form-content">
            <form onSubmit={handleSubmit} className="base-form">
              <div className="form-row editor-row">
                <div className="form-group">
                  <TemplateEditorWorkspace
                    editor={editor}
                    fontSize={fontSize}
                    onFontSizeChange={handleFontSizeChange}
                    isEditorEmpty={isEditorEmpty}
                    onUploadImage={uploadImage}
                    onUploadError={setError}
                  />
                </div>
              </div>

            </form>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="salon-button primary" disabled={loading} onClick={handleSubmit}>
            {loading ? (editingTemplate ? t('settings.updating') : t('settings.creating')) : (editingTemplate ? t('settings.updateTemplate') : t('settings.createTemplate'))}
          </button>
        </div>
      </div>

      {showUnsavedWarning && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-content" style={{ maxWidth: '450px', width: '90%', margin: '0 auto' }}>
            <div className="modal-header" style={{ padding: '20px 24px 16px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: '#374151' }}>{t('settings.unsavedChanges')}</h3>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px', lineHeight: '1.5' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>{t('settings.unsavedChangesMessage')}</p>
            </div>
            <div className="modal-footer" style={{ padding: '16px 24px 20px 24px', display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="salon-button secondary" onClick={handleCancelWarning} style={{ padding: '8px 16px', fontSize: '14px' }}>
                {t('settings.continueEditing')}
              </button>
              <button type="button" className="salon-button danger" onClick={handleDiscardChanges} style={{ padding: '8px 16px', fontSize: '14px' }}>
                {t('settings.discardChanges')}
              </button>
              <button type="button" className="salon-button primary" onClick={handleSaveAndClose} disabled={loading} style={{ padding: '8px 16px', fontSize: '14px' }}>
                {loading ? t('settings.saving') : t('settings.saveAndClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal message={error} onClose={() => setError('')} />
    </div>
  );
};

export default CreatePtoDecisionTemplateModal;
